import {
  PaperPlaneRightFill,
  PhoneAlt,
} from '@cat-home-experts/mortar-iconography-web';
import { Button } from '@checkatrade/web-ui';
import useIsMobileScreen from '@hooks/use-is-mobile-screen';
import useRecentlyContactedCompanyIds from '@hooks/use-recently-contacted-company-ids';
import { addToTradeContactHistory } from '@utils/trade-contact-history';
import { breakpointMedium, breakpointSmall } from 'lib/design-system';
import { spacing3 } from 'lib/mortar';
import { track } from 'lib/sdks/analytics';
import type { MouseEvent, MouseEventHandler } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import styled, { css } from 'styled-components';
import { RevealPhoneButton } from './RevealPhoneButton';

export const MAX_AB_WAIT_TIME = 5000;

type ContactCtaProps = {
  className?: string;
  company: { id: number; uniqueName: string; campaignId?: string };
  inlineOnDesktop?: boolean;
  phone: { number?: string | null; label?: string | null };
  prioritiseCalls: boolean;
  showMessageButton: boolean;
  type?: 'inline' | 'stacked';
  handleMessageClick: (e: MouseEvent) => void;
  onClickCall?: () => void;
  onClickRevealPhone?: () => void;
  sponsored?: boolean;
};

export function ContactCtas({
  className,
  company,
  inlineOnDesktop,
  phone,
  prioritiseCalls,
  showMessageButton,
  type = 'stacked',
  handleMessageClick,
  onClickCall,
  onClickRevealPhone,
  sponsored,
}: ContactCtaProps) {
  const [isClient, setIsClient] = useState(false);
  const contactedTradesHistory = useRecentlyContactedCompanyIds({
    account: true,
    local: true,
    fallbackToLocal: false,
  });
  const isMobile = useIsMobileScreen();

  const tradeHasBeenContacted = contactedTradesHistory.includes(company.id);
  const showCallButton = !!phone.number && !!phone.label;
  const showMessageCTA = showMessageButton;
  const showIcon = type === 'stacked';

  const handleCallClick: MouseEventHandler<HTMLButtonElement> = useCallback(
    e => {
      e.stopPropagation();
      track('global.call_trader', {
        trader_id: company.id,
        tel: phone.number,
        campaign_id: company.campaignId ?? null,
      });
      addToTradeContactHistory(company.id);
      onClickCall?.();
    },
    [company.campaignId, company.id, onClickCall, phone.number],
  );

  const handleRevealPhone = useCallback(() => {
    track('global.reveal_phone_number', {
      trader_id: company.id,
      campaign_id: company.campaignId ?? null,
    });
    onClickRevealPhone?.();
  }, [company.campaignId, company.id, onClickRevealPhone]);

  useEffect(() => {
    setIsClient(true);
  }, []);

  const messageIcon = <PaperPlaneRightFill size={20} color="" />;

  const messageText = 'Request a quote';

  const callButton = useMemo(() => {
    if (!isClient || !showCallButton) return null;
    if (!isMobile)
      return (
        <RevealPhoneButton
          phone={phone}
          data-guid="CallTrade"
          showIcon={showIcon}
          onCallClick={handleCallClick}
          onRevealPhone={handleRevealPhone}
          prioritiseCalls={prioritiseCalls}
        />
      );
    return (
      <Button
        className="text-xl"
        variant={!prioritiseCalls ? 'outline' : 'primary'}
        startContent={showIcon ? <PhoneAlt size={20} color="" /> : null}
        href={`tel:${phone.number}`}
        onClick={handleCallClick}
        data-guid="CallTrade"
      >
        <MobileOnly>Call</MobileOnly>
        <TabletOnly>{phone.label}</TabletOnly>
      </Button>
    );
  }, [
    handleCallClick,
    handleRevealPhone,
    isClient,
    isMobile,
    phone,
    showCallButton,
    showIcon,
    prioritiseCalls,
    sponsored,
  ]);

  const messageButton = !showMessageCTA ? null : (
    <Button
      className="text-xl"
      variant={prioritiseCalls ? 'outline' : 'primary'}
      startContent={showIcon ? messageIcon : null}
      onClick={handleMessageClick}
      data-guid={
        prioritiseCalls ? 'MessageTradeSubdued' : 'MessageTradePrimary'
      }
    >
      {tradeHasBeenContacted ? 'Message sent' : messageText}
    </Button>
  );

  if (!phone && !showMessageButton) {
    return null;
  }

  return (
    <CTAWrapper
      className={className}
      inlineOnDesktop={!!inlineOnDesktop}
      prioritiseCalls={prioritiseCalls}
      showCallButton={showCallButton}
      showMessageButton={showMessageButton}
      type={type}
    >
      {prioritiseCalls ? (
        <>
          {callButton}
          {messageButton}
        </>
      ) : (
        <>
          {messageButton}
          {callButton}
        </>
      )}
    </CTAWrapper>
  );
}

export const MobileOnly = styled.span<StyledSpan>`
  @media (min-width: ${breakpointMedium.start}) {
    display: none;
  }
`;

export const TabletOnly = styled.span<StyledSpan>`
  @media (max-width: ${breakpointSmall.end}) {
    display: none;
  }
`;

const CTAWrapper = styled.div<
  StyledDiv & {
    inlineOnDesktop: boolean;
    prioritiseCalls: boolean;
    showCallButton: boolean;
    showMessageButton: boolean;
    type: 'stacked' | 'inline';
  }
>`
  box-sizing: border-box;
  display: grid;
  grid-gap: ${spacing3};
  grid-template: 1fr 1fr / 1fr;
  min-width: 260px;
  width: 100%;
  grid-template-columns: ${({ type }) =>
    type === 'inline' ? '1fr 1fr' : null};
  grid-template-rows: ${({ type }) => (type === 'inline' ? '1fr' : '1fr 1fr')};
  padding-top: 12px;

  ${({ showCallButton, showMessageButton }) =>
    (!showCallButton || !showMessageButton) &&
    css`
      grid-template: 1fr / 1fr;
    `}

  ${({ inlineOnDesktop, prioritiseCalls }) =>
    inlineOnDesktop &&
    css`
      @media (min-width: ${breakpointMedium.start}) {
        grid-template: 1fr / 1fr 1fr;

        [data-guid='CallTrade'] {
          grid-column: ${prioritiseCalls ? `1` : `2`};
        }

        [data-guid='MessageTradePrimary'],
        [data-guid='MessageTradeSubdued'] {
          grid-column: ${prioritiseCalls ? `2` : `1`};
        }
      }
    `};
`;
